import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, AlertTriangle, Clock, User, ChevronDown, ChevronUp,
  Send, Eye, CheckCircle2, XCircle, RefreshCw, Pen, FileSignature
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/services/api';

// ============ CONSTANTS ============

const AMENDMENT_STATE_COLORS = {
  DRAFT: 'bg-gray-600/20 text-gray-400 border-gray-600/30',
  AWAITING_BUYER_SIGNATURE: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
  AWAITING_SELLER_SIGNATURE: 'bg-purple-600/20 text-purple-400 border-purple-600/30',
  FULLY_EXECUTED: 'bg-green-600/20 text-green-400 border-green-600/30',
  VOID: 'bg-red-600/20 text-red-400 border-red-600/30'
};

const AMENDMENT_TYPE_LABELS = {
  COMPLETION_DATE_CHANGE: 'Completion Date',
  PAYMENT_STRUCTURE_CHANGE: 'Payment Structure',
  PRICE_MODIFICATION: 'Price Change',
  DEPOSIT_MODIFICATION: 'Deposit Change',
  EXTENSION: 'Extension',
  TERMINATION_TERMS: 'Termination Terms',
  PARTY_CHANGE: 'Party Change',
  PROPERTY_MODIFICATION: 'Property Details',
  COMPENSATION_CHANGE: 'Commission Change',
  CUSTOM: 'Custom Amendment'
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// ============ SUB-COMPONENTS ============

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-primary/10 border-primary/30 text-primary',
    green: 'bg-green-950/20 border-green-600/30 text-green-400',
    yellow: 'bg-gold-950/20 border-gold-600/30 text-gold-400',
    gold: 'bg-gold-950/20 border-gold-600/30 text-gold-400',
    red: 'bg-red-950/20 border-red-600/30 text-red-400',
    blue: 'bg-blue-950/20 border-blue-600/30 text-blue-400',
    purple: 'bg-purple-950/20 border-purple-600/30 text-purple-400'
  };

  return (
    <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest opacity-70">{title}</p>
          <p className="text-xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs opacity-50">{subtitle}</p>}
        </div>
        {Icon && <Icon className="w-6 h-6 opacity-30" />}
      </div>
    </div>
  );
};

const AmendmentCard = ({ amendment, onNotifyParty, onViewDeal }) => {
  const [expanded, setExpanded] = useState(false);
  
  const awaitingRole = amendment.state === 'AWAITING_BUYER_SIGNATURE' ? 'Buyer' : 'Seller';
  const stateColor = AMENDMENT_STATE_COLORS[amendment.state] || AMENDMENT_STATE_COLORS.DRAFT;
  
  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className={`p-4 rounded-lg border ${stateColor} bg-opacity-50`}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/10">
                <FileSignature className="w-4 h-4" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">
                  {AMENDMENT_TYPE_LABELS[amendment.amendment_type] || amendment.amendment_type}
                </p>
                <p className="text-xs text-muted-foreground">
                  v{amendment.version} • Deal: {amendment.deal_id?.slice(0, 8).toUpperCase()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={stateColor}>
                {amendment.state?.replace(/_/g, ' ')}
              </Badge>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-4 pt-4 border-t border-white/10">
          <div className="space-y-4">
            {/* Amendment Details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Created</p>
                <p className="font-medium">{formatDate(amendment.created_at)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Initiated By</p>
                <p className="font-medium capitalize">{amendment.initiated_by_role}</p>
              </div>
              {amendment.financial_impact_aed && (
                <div>
                  <p className="text-muted-foreground text-xs">Financial Impact</p>
                  <p className={`font-medium ${amendment.financial_impact_aed > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {amendment.financial_impact_aed > 0 ? '+' : ''}{formatCurrency(amendment.financial_impact_aed)}
                  </p>
                </div>
              )}
              {amendment.time_impact_days && (
                <div>
                  <p className="text-muted-foreground text-xs">Time Impact</p>
                  <p className={`font-medium ${amendment.time_impact_days > 0 ? 'text-gold-400' : 'text-green-400'}`}>
                    {amendment.time_impact_days > 0 ? '+' : ''}{amendment.time_impact_days} days
                  </p>
                </div>
              )}
            </div>
            
            {/* Deltas */}
            <div>
              <p className="text-muted-foreground text-xs mb-2">Changes</p>
              <div className="space-y-2">
                {amendment.deltas?.map((delta, idx) => (
                  <div key={idx} className="p-2 rounded bg-white/5 text-sm">
                    <span className="text-muted-foreground">{delta.field_path}:</span>{' '}
                    <span className="text-red-400 line-through">{String(delta.old_value)}</span>{' '}
                    <span className="text-muted-foreground">→</span>{' '}
                    <span className="text-green-400">{String(delta.new_value)}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Reason */}
            <div>
              <p className="text-muted-foreground text-xs mb-1">Reason</p>
              <p className="text-sm">{amendment.amendment_reason || 'No reason provided'}</p>
            </div>
            
            {/* Signature Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-2 rounded border ${amendment.buyer_signed ? 'border-green-600/30 bg-green-950/10' : 'border-white/10 bg-white/5'}`}>
                <div className="flex items-center gap-2">
                  {amendment.buyer_signed ? (
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  ) : (
                    <Clock className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span className="text-sm">Buyer</span>
                </div>
                {amendment.buyer_signed && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(amendment.buyer_signed_at)}
                  </p>
                )}
              </div>
              <div className={`p-2 rounded border ${amendment.seller_signed ? 'border-green-600/30 bg-green-950/10' : 'border-white/10 bg-white/5'}`}>
                <div className="flex items-center gap-2">
                  {amendment.seller_signed ? (
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  ) : (
                    <Clock className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span className="text-sm">Seller</span>
                </div>
                {amendment.seller_signed && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(amendment.seller_signed_at)}
                  </p>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2 mt-4">
              {(amendment.state === 'AWAITING_BUYER_SIGNATURE' || amendment.state === 'AWAITING_SELLER_SIGNATURE') && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-gold-600/50 text-gold-400 hover:bg-gold-950/30"
                  onClick={() => onNotifyParty(amendment, awaitingRole)}
                  data-testid={`notify-party-btn-${amendment.amendment_id}`}
                >
                  <Send className="w-3 h-3 mr-1" />
                  Notify {awaitingRole}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-blue-600/50 text-blue-400 hover:bg-blue-950/30"
                onClick={() => onViewDeal(amendment.deal_id)}
                data-testid={`view-amendment-deal-btn-${amendment.deal_id}`}
              >
                <Eye className="w-3 h-3 mr-1" />
                View Deal
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ============ MAIN COMPONENT ============

export default function AmendmentsPanel() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [amendments, setAmendments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    awaitingBuyer: 0,
    awaitingSeller: 0,
    executed: 0,
    draft: 0
  });
  
  // Dialog state
  const [notifyDialog, setNotifyDialog] = useState({ open: false, amendment: null, party: null });
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Fetch pending amendments from new dedicated endpoint
      const res = await api.get('/v2/amendments/pending');
      const allAmendments = res.data?.pending_amendments || [];
      
      setAmendments(allAmendments);
      
      // Calculate stats from response or from data
      const stats_data = res.data?.by_state || {};
      setStats({
        total: allAmendments.length,
        awaitingBuyer: stats_data.AWAITING_BUYER_SIGNATURE || allAmendments.filter(a => a.state === 'AWAITING_BUYER_SIGNATURE').length,
        awaitingSeller: stats_data.AWAITING_SELLER_SIGNATURE || allAmendments.filter(a => a.state === 'AWAITING_SELLER_SIGNATURE').length,
        executed: allAmendments.filter(a => a.state === 'FULLY_EXECUTED').length,
        draft: stats_data.DRAFT || allAmendments.filter(a => a.state === 'DRAFT').length
      });
      
    } catch (error) {
      console.error('Failed to fetch amendments:', error);
      toast.error('Failed to load amendments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Amendments data refreshed');
  };

  const handleNotifyParty = async () => {
    if (!notifyDialog.amendment || !notifyDialog.party) return;
    
    setProcessing(true);
    try {
      // Send notification email using the new endpoint
      await api.post(`/v2/amendments/${notifyDialog.amendment.amendment_id}/notify-party`, null, {
        params: { party: notifyDialog.party.toLowerCase() }
      });
      
      toast.success(`Notification sent to ${notifyDialog.party}`);
      setNotifyDialog({ open: false, amendment: null, party: null });
    } catch (error) {
      console.error('Failed to send notification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDeal = (dealId) => {
    window.open(`/admin/deals/${dealId}`, '_blank');
  };

  // Filter amendments
  const filteredAmendments = amendments.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'buyer') return a.state === 'AWAITING_BUYER_SIGNATURE';
    if (filter === 'seller') return a.state === 'AWAITING_SELLER_SIGNATURE';
    if (filter === 'executed') return a.state === 'FULLY_EXECUTED';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="amendments-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" />
            Contract Amendments
          </h3>
          <p className="text-sm text-muted-foreground">
            Unsigned amendments blocking deal transitions
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-amendments-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard
          title="Total"
          value={stats.total}
          subtitle="Amendments"
          icon={FileText}
          color="primary"
        />
        <StatCard
          title="Awaiting Buyer"
          value={stats.awaitingBuyer}
          subtitle="Signature"
          icon={User}
          color="blue"
        />
        <StatCard
          title="Awaiting Seller"
          value={stats.awaitingSeller}
          subtitle="Signature"
          icon={User}
          color="purple"
        />
        <StatCard
          title="Draft"
          value={stats.draft}
          subtitle="Not submitted"
          icon={Pen}
          color="yellow"
        />
        <StatCard
          title="Executed"
          value={stats.executed}
          subtitle="Complete"
          icon={CheckCircle2}
          color="green"
        />
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="all">All ({amendments.length})</TabsTrigger>
          <TabsTrigger value="buyer">
            Awaiting Buyer ({stats.awaitingBuyer})
          </TabsTrigger>
          <TabsTrigger value="seller">
            Awaiting Seller ({stats.awaitingSeller})
          </TabsTrigger>
          <TabsTrigger value="executed">
            Executed ({stats.executed})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Amendments List */}
      <ScrollArea className="h-[500px] pr-4">
        {filteredAmendments.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileSignature className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No amendments found</p>
            <p className="text-xs mt-1">
              {filter === 'all' 
                ? 'Contract amendments will appear here when deals are modified'
                : `No amendments ${filter === 'executed' ? 'executed' : `awaiting ${filter} signature`}`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAmendments.map((amendment) => (
              <AmendmentCard
                key={amendment.amendment_id}
                amendment={amendment}
                onNotifyParty={(a, party) => setNotifyDialog({ open: true, amendment: a, party })}
                onViewDeal={handleViewDeal}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Notify Party Dialog */}
      <Dialog open={notifyDialog.open} onOpenChange={(open) => !open && setNotifyDialog({ open: false, amendment: null, party: null })}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-gold-400" />
              Send Signature Reminder
            </DialogTitle>
            <DialogDescription>
              Send an email reminder to the {notifyDialog.party?.toLowerCase()} to sign this amendment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 rounded bg-white/5 border border-white/10 space-y-2">
              <p className="text-sm">
                <span className="text-muted-foreground">Amendment:</span>{' '}
                <span className="font-medium">
                  {AMENDMENT_TYPE_LABELS[notifyDialog.amendment?.amendment_type] || 'Amendment'}
                </span>
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Deal:</span>{' '}
                <span className="font-mono text-primary">
                  {notifyDialog.amendment?.deal_id?.slice(0, 8).toUpperCase()}
                </span>
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Recipient:</span>{' '}
                <span className="text-gold-400">{notifyDialog.party}</span>
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNotifyDialog({ open: false, amendment: null, party: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleNotifyParty}
              disabled={processing}
              className="bg-gold-600 hover:bg-gold-700"
              data-testid="confirm-notify-btn"
            >
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reminder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
